import React, { useState } from 'react';
import Editor from '@monaco-editor/react';
import "../CodeConverter.css";
import { FaDownload, FaCompress, FaBrush, FaCopy, FaCheck, FaTrash } from "react-icons/fa";
import JsonEditorPanel from "../components/JsonEditorPanel";
import CommonSeoContent from "../components/CommonSeoContent";
import useEditorActions from "../utils/useEditorActions";

const CsvUploaderAndJsonConverter = () => {
  const [inputCode, setInputCode] = useState("");
  const [outputCode, setOutputCode] = useState("");

  // Import reusable actions
  const { handleDownload, handleClear } = useEditorActions(
    setInputCode,
    outputCode,
    setOutputCode
  );


  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file || !file.name.endsWith('.csv')) {
      setInputCode('Please upload a valid .csv file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      setInputCode(content);
    };
    reader.readAsText(file);
  };

  // Convert CSV to JSON
  const convertCsvToJson = () => {
    try {
      const lines = inputCode.trim().split(/\r?\n/);
      const headers = lines[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
      const json = lines.slice(1).map((line) => {
        const values = line.split(',').map((v) => v.trim().replace(/^"|"$/g, ''));
        const obj = {};
        headers.forEach((h, i) => {
          obj[h] = values[i] ?? '';
        });
        return obj;
      });

      setOutputCode(JSON.stringify(json, null, 2));
    } catch (e) {
      setOutputCode('Error parsing CSV: ' + e.message);
    }
  };

    return (
      <div className="formatter-container">
        <h1>CSV ➝ JSON Converter </h1>

        {/* Toolbar */}
        <div className="toolbar">
          <input type="file" accept=".csv" onChange={handleFileUpload} />
          <button onClick={convertCsvToJson} disabled={!inputCode} className="btn primary">
            <FaBrush /> Convert
          </button>
          <button onClick={() => handleDownload("c", "formatted-c")} className="btn success">
            <FaDownload /> Download
          </button>
        </div>

        {/* Editors */}
        <div className="editor-panels">
          <JsonEditorPanel title="Input CSV" language="csv" code={inputCode} setCode={setInputCode} />
          <div className="editor-section-empty-div">
          </div>
          <JsonEditorPanel title="Output JSON" language="json"  code={outputCode} />
        </div>
		
		<CommonSeoContent
		  title="Online CSV to JSON Converter"
		  description="Convert CSV data into JSON format instantly with jsonconverter.org. Supports headers, nested structures, and produces clean JSON output for developers and data professionals."
		  keywords="CSV to JSON, csv to json converter, csv to json online, convert csv to json, json generator, data converter, csv parser, jsonconverter.org, online json converter"
		  author="jsonconverter.org"
		  heading="Why Use CSV to JSON Converter?"
		  whyUseText="Our CSV to JSON Converter makes it quick and easy to transform tabular CSV data into structured JSON format. Whether you're importing CSV files into APIs, applications, or databases that require JSON input, this tool helps you streamline the process with just a few clicks. It supports headers, multiple rows, and properly formats the output as readable and valid JSON. Everything runs directly in your browser, so there’s no need to upload files or install software. Your data stays private, conversion is instant, and it’s 100% free — perfect for developers, analysts, and anyone working with data formats."
		  howToUseSteps={[
			"Paste your CSV data into the input editor",
			"OR Upload your CSV file for conversion",
			"Click the Convert button",
			"View your converted JSON output instantly",
			"Copy or download the JSON for use in your apps or tools.",
		  ]}
		  features={[
			"Converts CSV with or without headers into valid JSON",
			"Supports multiple rows and automatically maps key-value pairs",
			"Produces clean, human-readable JSON with proper formatting",
			"Handles quoted values, commas inside fields, and special characters",
			"Browser-based — no server processing, your data stays secure",
			"Free to use — no installation, no signup required",
			"Download JSON for offline use or direct import",
		  ]}
		  inputTitle="Input CSV:"
		  inputExample={`
name,age,email,profile.bio,profile.website
"John Doe","30","john@example.com","Developer","https://jsonconverter.org"
"Jane Smith","25","jane@example.com","Designer","https://janesmith.dev"`}
		  outputTitle="Output Json:"
		  outputExample={`
[
  {
    "name": "John Doe",
    "age": "30",
    "email": "john@example.com",
    "profile.bio": "Developer",
    "profile.website": "https://jsonconverter.org"
  },
  {
    "name": "Jane Smith",
    "age": "25",
    "email": "jane@example.com",
    "profile.bio": "Designer",
    "profile.website": "https://janesmith.dev"
  }
]`}
		  callToAction="Start converting your CSV to JSON instantly — no installation or login required. Try it now on jsonconverter.org!"
		/>  
      </div>
    );
};

export default CsvUploaderAndJsonConverter;
